import React from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/images/logo_black.png';
import { 
  Envelope, 
  MapPin,
  ArrowRight
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { t } = useTranslation();
  
  const serviceLinks = [
    { name: t('header.websites'), href: '/webdesign' },
    { name: t('header.webshops'), href: '/webshops' },
    { name: t('header.seo'), href: '/seo' },
    { name: t('header.websiteSpeed'), href: '/website-snelheid' },
  ];

  const companyLinks = [
    { name: t('header.aboutUs'), href: '/about' },
    { name: t('header.portfolio'), href: '/portfolio' },
    { name: t('header.partner'), href: '/partner' },
    { name: t('header.contact'), href: '/contact' },
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img src={logo} alt="Juliux Logo" className="h-8 invert" />
            </Link>
            <p className="text-gray-300 mb-6 leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="space-y-3">
              <div className="flex items-center text-gray-300">
                <Envelope className="ph-thin w-4 h-4 mr-3 text-blue-400" />
                <span className="text-sm">{t('footer.email')}</span>
              </div>
              <div className="flex items-center text-gray-300">
                <MapPin className="ph-thin w-4 h-4 mr-3 text-blue-400" />
                <span className="text-sm">{t('footer.location')}</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">{t('footer.services')}</h3>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors text-sm flex items-center group"
                  >
                    <ArrowRight className="ph-thin w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">{t('footer.company')}</h3>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors text-sm flex items-center group"
                  >
                    <ArrowRight className="ph-thin w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA Section */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">{t('footer.readyToStart')}</h3>
            <p className="text-gray-300 mb-6 text-sm leading-relaxed">
              {t('footer.readyToStartDescription')}
            </p>
            <Link
              to="/start-your-project"
              className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors text-sm group"
            >
              {t('footer.startProject')}
              <ArrowRight className="ph-thin w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-6">
              <Link to="/privacy-policy" className="text-sm text-gray-400 hover:text-white transition-colors">
                {t('footer.privacyPolicy')}
              </Link>
              <button 
                onClick={() => {
                  // Clear saved consent to show cookie banner again
                  localStorage.removeItem('cookie-consent');
                  window.location.reload();
                }}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                {t('footer.cookieSettings')}
              </button>
            </div>
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <span>&copy; {new Date().getFullYear()} Juliux. {t('footer.copyright')}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;